import { parseDbTimestamp } from "@repo/shared";
import type { ClientClassificationInput, ClientClassificationType } from "@repo/shared/types";
import { History } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { T } from "./client-classification-widget.i18n";
import {
  type ActingRepresentative,
  RepresentativeNote,
  representativeNoteText,
} from "./representative-note";

// QL-668 — a sentinel distinct from any real `buildInput()` JSON (including
// the coerced-false Company shape), so priming on an unanswered Company
// never equals the key an actual "No" click would produce.
const UNANSWERED_STATE = "__unanswered__";

type ClientClassificationWidgetProps = {
  clientType: "Person" | "Company";
  /** QP-184 — Art. 54(6): K&E-related criteria refer to the acting representative. */
  actingRepresentative?: ActingRepresentative | null;
  /** QP-184 — raw type is Group (suitability path is Person; wording differs). */
  isGroupClient?: boolean;
  clientName: string;
  inputData?: string | null;
  onChange: (input: ClientClassificationInput) => void;
  disabled?: boolean;
  // Public flow: raw previous answer + browser-side use-callback (legacy path, ref-primed below).
  previousAnswer?: {
    inputData: string;
    timestamp: string | null;
  } | null;
  onUsePreviousAnswer?: () => Promise<void>;
  // Advisor flow: backend-sourced reuse. `available`/`sourceDate` come from the reuse resolver;
  // `onApply` calls the apply mutation and the recomputed value flows back via `inputData`.
  available?: boolean;
  sourceDate?: string | null;
  onApply?: () => Promise<void>;
  // Configurable styling props
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  // Follow-up A — H2 (error highlighting). When true, the widget renders a destructive
  // border on its outer container and a "this widget needs attention" message under the
  // title. Pure UI signal; the engine itself decides what counts as missing.
  hasValidationError?: boolean;
};

export function ClientClassificationWidget({
  clientType,
  actingRepresentative,
  isGroupClient = false,
  clientName: _clientName,
  inputData,
  onChange,
  disabled = false,
  previousAnswer,
  onUsePreviousAnswer,
  available = false,
  sourceDate,
  onApply,
  style = "none",
  title,
  description,
  hasValidationError = false,
}: ClientClassificationWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const { formatDate } = useRegionalFormat();
  const t = useT(T);
  // Person state (opt-up)
  const [wantsOptUp, setWantsOptUp] = useState(false);
  const [hasSignificantTransactions, setHasSignificantTransactions] = useState(false);
  const [hasLargePortfolio, setHasLargePortfolio] = useState(false);
  const [hasFinanceExperience, setHasFinanceExperience] = useState(false);

  // Company state
  const [isFinancialInstitution, setIsFinancialInstitution] = useState<boolean | null>(null);
  const [hasLargeBalanceSheet, setHasLargeBalanceSheet] = useState(false);
  const [hasLargeRevenue, setHasLargeRevenue] = useState(false);
  const [hasLargeEquity, setHasLargeEquity] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);
  const [hasDecided, setHasDecided] = useState(false);
  const [isApplyingPrevious, setIsApplyingPrevious] = useState(false);
  const [reuseError, setReuseError] = useState<string | null>(null);
  const onChangeRef = useRef(onChange);

  // Reuse can be offered via the backend (advisor: `available` + `onApply`) or the legacy
  // browser path (public: `previousAnswer` + `onUsePreviousAnswer`).
  const canReuse = (available && !!onApply) || (!!previousAnswer && !!onUsePreviousAnswer);
  const reuseDate = sourceDate ?? previousAnswer?.timestamp ?? null;

  // Determine if we should show the choice screen
  const showChoiceScreen = canReuse && !disabled && !inputData && !hasDecided && !reuseError;

  // Keep the ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Ref to track last saved input state to prevent duplicate saves
  const lastSavedStateRef = useRef<string | null>(null);
  // Track last parsed inputData to detect when prop changes
  const lastInputDataRef = useRef<string | null>(null);

  // Parse inputData to populate local checkbox states
  useEffect(() => {
    if (inputData && inputData !== lastInputDataRef.current) {
      try {
        const parsed: ClientClassificationInput = JSON.parse(inputData);

        if (parsed.personAnswers) {
          setWantsOptUp(parsed.personAnswers.wantsOptUp);
          setHasSignificantTransactions(parsed.personAnswers.hasSignificantTransactions);
          setHasLargePortfolio(parsed.personAnswers.hasLargePortfolio);
          setHasFinanceExperience(parsed.personAnswers.hasFinanceExperience);
        }

        if (parsed.companyAnswers) {
          setIsFinancialInstitution(parsed.companyAnswers.isFinancialInstitution);
          setHasLargeBalanceSheet(parsed.companyAnswers.hasLargeBalanceSheet);
          setHasLargeRevenue(parsed.companyAnswers.hasLargeRevenue);
          setHasLargeEquity(parsed.companyAnswers.hasLargeEquity);
        }

        lastSavedStateRef.current = inputData;
        lastInputDataRef.current = inputData;

        setHasDecided(true);
        setIsInitialized(true);
      } catch (err) {
        log.error({ err }, "failed to parse client classification value");
      }
    } else if (!inputData && !isInitialized && !showChoiceScreen) {
      setIsInitialized(true);
    }
  }, [inputData, isInitialized, showChoiceScreen]);

  // Build input object from current local state
  const buildInput = useCallback((): ClientClassificationInput => {
    const input: ClientClassificationInput = { clientType };

    if (clientType === "Person") {
      input.personAnswers = {
        wantsOptUp,
        hasSignificantTransactions,
        hasLargePortfolio,
        hasFinanceExperience,
      };
    } else {
      input.companyAnswers = {
        isFinancialInstitution: isFinancialInstitution ?? false,
        hasLargeBalanceSheet,
        hasLargeRevenue,
        hasLargeEquity,
      };
    }

    return input;
  }, [
    clientType,
    wantsOptUp,
    hasSignificantTransactions,
    hasLargePortfolio,
    hasFinanceExperience,
    isFinancialInstitution,
    hasLargeBalanceSheet,
    hasLargeRevenue,
    hasLargeEquity,
  ]);

  // Local classification for real-time UI feedback (no round-trip)
  const calculateClassification = useCallback((): ClientClassificationType => {
    if (clientType === "Person") {
      if (wantsOptUp && hasSignificantTransactions && hasLargePortfolio && hasFinanceExperience) {
        return "Professional";
      }
      return "Retail";
    }

    if (isFinancialInstitution === true) {
      return "Eligible Counterparty";
    }

    if (isFinancialInstitution === false) {
      const criteriaCount = [hasLargeBalanceSheet, hasLargeRevenue, hasLargeEquity].filter(
        Boolean,
      ).length;
      if (criteriaCount >= 2) {
        return "Professional";
      }
    }

    return "Retail";
  }, [
    clientType,
    wantsOptUp,
    hasSignificantTransactions,
    hasLargePortfolio,
    hasFinanceExperience,
    isFinancialInstitution,
    hasLargeBalanceSheet,
    hasLargeRevenue,
    hasLargeEquity,
  ]);

  // Auto-save: send inputData to backend when answers change.
  // QL-668 — never on mount: with nothing stored, the server owns the default
  // (Retail for a person/group without an earlier classification, no default
  // for a company), so the untouched form is primed as "already saved" and only
  // a real change saves. "Start fresh" saves explicitly (handleStartFresh). A company
  // whose financial-institution question is unanswered has no answer to save.
  useEffect(() => {
    if (!isInitialized || showChoiceScreen || disabled) return;

    const unanswered = clientType === "Company" && isFinancialInstitution === null;
    const stateKey = unanswered ? UNANSWERED_STATE : JSON.stringify(buildInput());

    if (lastSavedStateRef.current === null) {
      lastSavedStateRef.current = stateKey;
      return;
    }
    if (unanswered) return;
    if (stateKey !== lastSavedStateRef.current) {
      lastSavedStateRef.current = stateKey;
      onChangeRef.current(buildInput());
    }
  }, [isInitialized, buildInput, showChoiceScreen, disabled, clientType, isFinancialInstitution]);

  const handleUsePrevious = async () => {
    setIsApplyingPrevious(true);
    setReuseError(null);
    try {
      if (onApply) {
        // Advisor path: the backend applies and recomputes. No local state priming — the
        // recomputed value flows back through `inputData` and the parse effect above converges
        // (without marking the form dirty or re-saving).
        await onApply();
        setHasDecided(true);
      } else if (onUsePreviousAnswer && previousAnswer) {
        // Public path (legacy): prime local state from the raw previous answer, then save.
        const parsed: ClientClassificationInput = JSON.parse(previousAnswer.inputData);

        if (parsed.personAnswers) {
          setWantsOptUp(parsed.personAnswers.wantsOptUp);
          setHasSignificantTransactions(parsed.personAnswers.hasSignificantTransactions);
          setHasLargePortfolio(parsed.personAnswers.hasLargePortfolio);
          setHasFinanceExperience(parsed.personAnswers.hasFinanceExperience);
        }

        if (parsed.companyAnswers) {
          setIsFinancialInstitution(parsed.companyAnswers.isFinancialInstitution);
          setHasLargeBalanceSheet(parsed.companyAnswers.hasLargeBalanceSheet);
          setHasLargeRevenue(parsed.companyAnswers.hasLargeRevenue);
          setHasLargeEquity(parsed.companyAnswers.hasLargeEquity);
        }

        lastSavedStateRef.current = previousAnswer.inputData;
        lastInputDataRef.current = previousAnswer.inputData;

        setHasDecided(true);
        setIsInitialized(true);

        await onUsePreviousAnswer();
      }
    } catch (err) {
      log.error({ err }, "failed to apply previous client classification answer");
      // Advisor refusal (e.g. instance completed in another tab) — hide the button and surface a
      // message; the apply mutation also refetches availability so it stays hidden.
      if (onApply) setReuseError(t.reuseNoLongerAvailable);
    } finally {
      setIsApplyingPrevious(false);
    }
  };

  const handleStartFresh = () => {
    setHasDecided(true);
    setIsInitialized(true);
    // QL-668 — a client with an earlier classification has no server default,
    // so "Start fresh" is the answer for a person/group: save the untouched
    // (Retail) form now, or the choice screen returns on the next mount and the
    // component stays unanswered. A company still has to answer the
    // financial-institution question first.
    if (clientType !== "Company") {
      const input = buildInput();
      lastSavedStateRef.current = JSON.stringify(input);
      onChangeRef.current(input);
    }
  };

  // Get container class based on style. When `hasValidationError` is true, override to a
  // bordered destructive container regardless of `style` (H2 — D11).
  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-6";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-6";
      case "box":
        return "rounded-lg p-6 space-y-6 bg-muted/50";
      default:
        return "space-y-6";
    }
  };

  const getClassificationExplanation = (classification: ClientClassificationType): string => {
    if (clientType === "Person") {
      if (classification === "Professional") {
        return t.personProfessionalExplanation;
      }
      if (wantsOptUp) {
        const met = [hasSignificantTransactions, hasLargePortfolio, hasFinanceExperience].filter(
          Boolean,
        ).length;
        return t.personAllCriteriaRequired(met);
      }
      return isGroupClient ? t.groupRetailExplanation : t.personRetailExplanation;
    }

    // Company
    if (classification === "Eligible Counterparty") {
      return t.companyEligibleCounterparty;
    }
    if (classification === "Professional") {
      const met = [hasLargeBalanceSheet, hasLargeRevenue, hasLargeEquity].filter(Boolean).length;
      return t.companyProfessional(met);
    }
    if (isFinancialInstitution === false) {
      const met = [hasLargeBalanceSheet, hasLargeRevenue, hasLargeEquity].filter(Boolean).length;
      return t.companyRetailProgress(met);
    }
    return t.pleaseAnswer;
  };

  const classification = calculateClassification();
  const personCriteriaMet = [
    hasSignificantTransactions,
    hasLargePortfolio,
    hasFinanceExperience,
  ].filter(Boolean).length;
  const companyCriteriaMet = [hasLargeBalanceSheet, hasLargeRevenue, hasLargeEquity].filter(
    Boolean,
  ).length;

  // Choice screen - show before form
  if (showChoiceScreen) {
    const formattedDate = formatDate(parseDbTimestamp(reuseDate));
    return (
      <div className={getContainerClass()}>
        <FlowComponentHeader title={title} description={description} />
        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {/* Choice UI */}
        <div className="space-y-4">
          <div className="text-center py-4">
            <History className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-medium text-lg mb-2">{t.previousAnswerFound}</h3>
            <p className="text-sm text-muted-foreground">{t.answeredOn(formattedDate)}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleUsePrevious} disabled={isApplyingPrevious} className="gap-2">
              {isApplyingPrevious ? t.applying : t.usePreviousAnswer}
            </Button>
            <Button
              variant="outline"
              onClick={handleStartFresh}
              disabled={isApplyingPrevious}
              className="gap-2"
            >
              {t.startFresh}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={getContainerClass()}>
      <FlowComponentHeader title={title} description={description} />
      {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}
      {reuseError && <p className="text-sm text-destructive">{reuseError}</p>}
      {actingRepresentative && (
        <RepresentativeNote
          text={representativeNoteText(
            t.repNote,
            actingRepresentative,
            t.repGeneric,
            userLanguage,
            t.repNoteViaMember,
          )}
        />
      )}

      {/* Person: Opt-up section */}
      {clientType === "Person" && (
        <div className="space-y-4">
          <div className="space-y-3">
            <OptionRow>
              <Checkbox
                id="wants-opt-up"
                checked={wantsOptUp}
                onCheckedChange={(checked) => setWantsOptUp(checked === true)}
                disabled={disabled}
              />
              <Label htmlFor="wants-opt-up" className="font-medium cursor-pointer">
                {t.wantsProfessionalClient}
              </Label>
            </OptionRow>

            {wantsOptUp && (
              <div className="ml-6 space-y-4 pt-2 border-l-2 pl-4">
                <div className="text-sm text-muted-foreground mb-3">{t.toQualifyAllCriteria}</div>

                <div>
                  <OptionRow>
                    <Checkbox
                      id="transactions"
                      checked={hasSignificantTransactions}
                      onCheckedChange={(checked) => setHasSignificantTransactions(checked === true)}
                      disabled={disabled}
                    />
                    <Label
                      htmlFor="transactions"
                      className="font-normal cursor-pointer leading-tight"
                    >
                      {t.significantTransactions}
                    </Label>
                  </OptionRow>

                  <OptionRow>
                    <Checkbox
                      id="portfolio"
                      checked={hasLargePortfolio}
                      onCheckedChange={(checked) => setHasLargePortfolio(checked === true)}
                      disabled={disabled}
                    />
                    <Label htmlFor="portfolio" className="font-normal cursor-pointer leading-tight">
                      {t.largePortfolio}
                    </Label>
                  </OptionRow>

                  <OptionRow>
                    <Checkbox
                      id="experience"
                      checked={hasFinanceExperience}
                      onCheckedChange={(checked) => setHasFinanceExperience(checked === true)}
                      disabled={disabled}
                    />
                    <Label
                      htmlFor="experience"
                      className="font-normal cursor-pointer leading-tight"
                    >
                      {t.financeExperience}
                    </Label>
                  </OptionRow>
                </div>

                {/* Progress indicator */}
                <div className="flex items-center gap-2 pt-2">
                  <div className="text-sm text-muted-foreground">{t.progress}</div>
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className={`w-6 h-2 rounded ${
                          i < personCriteriaMet ? "bg-primary" : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t.ofCriteria(personCriteriaMet)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company: Classification questions */}
      {clientType === "Company" && (
        <div className="space-y-4">
          {/* Financial institution question */}
          <div className="space-y-3">
            <Label className="text-base font-medium">{t.isFinancialInstitution}</Label>
            <p className="text-sm text-muted-foreground">{t.financialInstitutionExamples}</p>
            <RadioGroup
              value={isFinancialInstitution === null ? "" : isFinancialInstitution ? "yes" : "no"}
              onValueChange={(val) => setIsFinancialInstitution(val === "yes")}
              disabled={disabled}
              className="flex gap-4"
            >
              <OptionRow>
                <RadioGroupItem value="yes" id="fi-yes" />
                <Label htmlFor="fi-yes" className="font-normal cursor-pointer">
                  {t.yes}
                </Label>
              </OptionRow>
              <OptionRow>
                <RadioGroupItem value="no" id="fi-no" />
                <Label htmlFor="fi-no" className="font-normal cursor-pointer">
                  {t.no}
                </Label>
              </OptionRow>
            </RadioGroup>
          </div>

          {/* Large company criteria - only shown if not financial institution */}
          {isFinancialInstitution === false && (
            <div className="space-y-4 pt-2 border-t">
              <div>
                <Label className="text-base font-medium">{t.largeCompanyCriteria}</Label>
                <p className="text-sm text-muted-foreground mt-1">{t.largeCompanyDescription}</p>
              </div>

              <div>
                <OptionRow>
                  <Checkbox
                    id="balance-sheet"
                    checked={hasLargeBalanceSheet}
                    onCheckedChange={(checked) => setHasLargeBalanceSheet(checked === true)}
                    disabled={disabled}
                  />
                  <Label
                    htmlFor="balance-sheet"
                    className="font-normal cursor-pointer leading-tight"
                  >
                    {t.balanceSheet}
                  </Label>
                </OptionRow>

                <OptionRow>
                  <Checkbox
                    id="revenue"
                    checked={hasLargeRevenue}
                    onCheckedChange={(checked) => setHasLargeRevenue(checked === true)}
                    disabled={disabled}
                  />
                  <Label htmlFor="revenue" className="font-normal cursor-pointer leading-tight">
                    {t.netRevenue}
                  </Label>
                </OptionRow>

                <OptionRow>
                  <Checkbox
                    id="equity"
                    checked={hasLargeEquity}
                    onCheckedChange={(checked) => setHasLargeEquity(checked === true)}
                    disabled={disabled}
                  />
                  <Label htmlFor="equity" className="font-normal cursor-pointer leading-tight">
                    {t.ownFunds}
                  </Label>
                </OptionRow>
              </div>

              {/* Progress indicator */}
              <div className="flex items-center gap-2">
                <div className="text-sm text-muted-foreground">{t.progress}</div>
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={`w-6 h-2 rounded ${
                        i < companyCriteriaMet
                          ? companyCriteriaMet >= 2
                            ? "bg-primary"
                            : "bg-warning"
                          : "bg-muted"
                      }`}
                    />
                  ))}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t.ofCriteria(companyCriteriaMet)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Classification Result */}
      <div className="pt-4 border-t">
        <div className="text-sm font-medium text-muted-foreground mb-2">
          {t.classificationResult}
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1">
            {classification}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {getClassificationExplanation(classification)}
          </span>
        </div>
      </div>
    </div>
  );
}
